import { useState, type CSSProperties, type FormEvent } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import { invokeFunction } from '@/lib/functionsInvoke';
import type { NewsletterSignupBlock } from '@/types/blocks';

const HEADING_STYLE: CSSProperties = { color: 'var(--color-heading, #0f172a)', fontFamily: 'var(--font-heading, var(--font-display))' };
const PARAGRAPH_STYLE: CSSProperties = { color: 'var(--color-paragraph, #475569)', fontFamily: 'var(--font-paragraph, var(--font-body))' };

/**
 * Formulário de inscrição em newsletter no site público. Double opt-in: esta
 * chamada só grava o assinante como `pending` e dispara o email de
 * confirmação — quem confirma de fato é o link nesse email (newsletter-confirm).
 *
 * Diferente de SignupScreen.tsx/LoginScreen.tsx, o widget aqui SEMPRE renderiza,
 * mesmo em `npm run dev` — este é o primeiro endpoint anon-writable do projeto
 * (comentário em newsletter-subscribe/index.ts) e a Edge Function não tem bypass
 * de ambiente: se `TURNSTILE_SECRET_KEY` estiver configurada, um token válido é
 * sempre exigido. `VITE_TURNSTILE_SITE_KEY` já usa a test key documentada da
 * Cloudflare (`1x00000000000000000000AA`) em dev, que passa automaticamente sem
 * precisar de domínio verificado.
 */
export function PublicNewsletterSignup({ block, siteId, className = '' }: { block: NewsletterSignupBlock; siteId?: string; className?: string }) {
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!siteId) return;
    const trimmed = email.trim();
    if (!trimmed) {
      setStatus('error');
      setErrorMessage('Informe um email.');
      return;
    }
    if (!turnstileToken) {
      setStatus('error');
      setErrorMessage('Complete a validação anti-robô.');
      return;
    }

    setStatus('loading');
    setErrorMessage('');
    try {
      await invokeFunction('newsletter-subscribe', {
        body: { site_id: siteId, email: trimmed, turnstile_token: turnstileToken },
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Não foi possível concluir a inscrição. Tente novamente.');
    }
  };

  if (status === 'success') {
    return (
      <div className={`mx-auto max-w-md space-y-2 rounded-xl border border-slate-200/80 bg-white p-6 text-center shadow-sm ${className}`}>
        <CheckCircle2 className="mx-auto h-6 w-6 text-emerald-500" />
        <p className="text-sm" style={PARAGRAPH_STYLE}>{block.successMessage}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`mx-auto max-w-md space-y-3 rounded-xl border border-slate-200/80 bg-white p-6 text-center shadow-sm ${className}`}>
      {block.title && <h3 className="font-display text-lg font-bold" style={HEADING_STYLE}>{block.title}</h3>}
      {block.description && <p className="text-sm" style={PARAGRAPH_STYLE}>{block.description}</p>}
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seuemail@exemplo.com"
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none"
        />
        <button
          type="submit"
          disabled={status === 'loading' || !siteId}
          className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {status === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
          {status === 'loading' ? 'Enviando...' : block.buttonLabel || 'Inscrever-se'}
        </button>
      </div>
      <div className="flex justify-center">
        <Turnstile
          siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
          onSuccess={setTurnstileToken}
          options={{ theme: 'auto', language: 'pt-br', size: 'flexible' }}
        />
      </div>
      {status === 'error' && errorMessage && <p className="text-xs text-red-600">{errorMessage}</p>}
    </form>
  );
}
